import { useEffect, useRef, useState } from "react";
import { onGeoPulse } from "../net/popSocket";
import { usePopStore } from "../state/usePopStore";
import { useSettingsStore } from "../state/useSettingsStore";
import { countryFlag, countryName } from "../lib/geo";

// Ambient proof that the hall is shared: when a sync carries country deltas,
// the flags of whoever just popped drift up from the corner. Strangers only —
// your own country is filtered out (your feedback is the balloon itself), and
// so is "XX", which has no flag to show. Decorative by design: aria-hidden,
// capped, and absent entirely under reduced motion.

type Chip = { id: number; c: string; n: number };

/** Matches the geo-chip CSS animation length. */
const CHIP_TTL_MS = 2600;
const MAX_CHIPS = 5;
/** At most this many new chips per pulse — a busy world stays a whisper. */
const PER_PULSE = 3;

export default function GeoSparkles() {
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);
  const [chips, setChips] = useState<Chip[]>([]);
  const nextId = useRef(1);

  useEffect(() => {
    if (reducedMotion) return;
    const off = onGeoPulse((pulse) => {
      const me = usePopStore.getState().myCountry;
      const fresh = pulse
        .filter((p) => p.c !== "XX" && p.c !== me)
        .slice(0, PER_PULSE)
        .map((p) => ({ id: nextId.current++, c: p.c, n: p.n }));
      if (fresh.length === 0) return;
      setChips((cur) => [...cur, ...fresh].slice(-MAX_CHIPS));
      const ids = new Set(fresh.map((f) => f.id));
      setTimeout(() => {
        setChips((cur) => cur.filter((ch) => !ids.has(ch.id)));
      }, CHIP_TTL_MS);
    });
    return off;
  }, [reducedMotion]);

  if (reducedMotion || chips.length === 0) return null;
  return (
    <div className="geo-sparkles" aria-hidden="true">
      {chips.map((ch) => (
        <span key={ch.id} className="geo-chip" title={countryName(ch.c)}>
          <span className="geo-chip-flag">{countryFlag(ch.c)}</span>
          <span className="geo-chip-n">+{ch.n.toLocaleString("en-US")}</span>
        </span>
      ))}
    </div>
  );
}
